import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import TextField from "@/common/components/TextField";
import { useCities } from "@/common/hooks/queries/useCities";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { StateData } from "@/common/types/address";
import { Building, MapIcon, SearchIcon } from "lucide-react";
import Table from "@/common/components/Table";
import { useGeoStates } from "@/common/hooks/queries/useGeoStates";
import { useProductCategories } from "@/common/hooks/queries/useProductCategories";
import { ProductCategoryData } from "@/common/types/productCategory.type";
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies";
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer";
import { defaultColumns } from "@/features/establishments/constants/establishmentCompanyTableColumns.constant";
import Button from "@/common/components/Button";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { GeoTransformers } from "@/common/services/geoTransformers";
import { statusOptions } from "../../../../../users/constants/statusOptions.constant";
import { commercialKindOptions } from "../../../../../establishmentLeadsFunnel/constants/commercialKindOptions.constant";
import { useQueryClient } from "@tanstack/react-query";
import BulkActionNewEntryModal from "@/common/components/BulkActionNewEntryModal";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { useFilterDate } from "@/common/hooks/useFilterDate";

interface ListProps {
  setView: (view: "map" | "list") => void;
}

const List = ({ setView }: ListProps) => {
  const { filterParams, setFilterParams } = useFilterParams();
  const { date, formattedDate } = useFilterDate(filterParams)

  const queryClient = useQueryClient();

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const [searchProductCategory, setSearchProductCategory] = useDebounce("");
  const [searchState, setSearchState] = useDebounce("");
  const [searchCity, setSearchCity] = useDebounce("");

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategory, sort: { display_name: 'asc' } },
    mapper: Mapper.mapToOptions<ProductCategoryData>({ valueFieldName: "id", labelFieldName: "display_name" }),
  });

  const { data: stateOptions } = useGeoStates({
    filters: { search: searchState },
    mapper: Mapper.mapToOptions<StateData>({ valueFieldName: "id", labelFieldName: "display_name" }),
  });

  const { data: cityOptions } = useCities({
    filters: { search: searchCity, stateId: filterParams.state?.value },
    mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
  });

  const {
    data: establishmentCompanies,
    meta,
    isLoading: isLoadingEstablishmentCompanies,
    error: establishmentCompaniesError = {} as any,
    refetch: refetchEstablishmentCompanies,
  } = useEstablishmentCompanies({
    filters: {
      search: search,
      page: filterParams.page || 1,
      productCategoryId: filterParams.productCategory?.value,
      status: filterParams.status?.value,
      stateId: filterParams.state?.value,
      cityId: filterParams.city?.value,
      commercialKind: filterParams.commercialKind?.value,
      lastConsumptionCreatedStartDate: formattedDate.from,
      lastConsumptionCreatedEndDate: formattedDate.to,
    },
    mapper: EstablishmentCompanyTransformer.tableTransformer("Admin::Establishment::Companies::Filter"),
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<Building className="w-10 h-10" strokeWidth={1.5} />}
        title="Rede credenciada"
        actions={(
          <div className="flex gap-4">
            <BulkActionNewEntryModal
              operationKlassToCheckPermission="Admin::BulkActions::Entries::RequestTemplate"
              options={[
                {
                  label: 'Editar data de vencimento de fatura',
                  actionerKlass:
                    'BulkActions::Admin::EstablishmentsBillInformationEditor',
                },
                {
                  label: 'Importação de empresas de estabelecimento',
                  actionerKlass:
                    'BulkActions::Admin::EstablishmentCompaniesImporter',
                },
              ]}
              onEntryCompleted={() => {
                queryClient.invalidateQueries({
                  queryKey: ['establishmentCompanies'],
                });
              }}
            />

            <Button
              name="switch_view"
              variant="neutral"
              className="text-sm py-4 shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onClick={() => setView('map')}
            >
              <MapIcon size={18} strokeWidth={1.5} />

              Ver em mapa
            </Button>
          </div>
        )}
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-192px)]"
        statusCode={establishmentCompaniesError?.status}
        refetchFunction={refetchEstablishmentCompanies}
      >
        <div className="h-[calc((100vh-52px-4rem-1.5rem))] flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center">
              <TextField
                name="search"
                LeftIcon={<SearchIcon />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)] w-full"
                onChange={(e) => {
                  setSearch((e.target as HTMLInputElement).value);
                  setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
                }}
                value={filterParams.search}
              />
            </div>

            <div className="flex gap-2 items-center justify-start">
              <DatePickerWithRange placeholder="Período com transação" date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

              <ListFilters
                filters={[
                  {
                    className: "w-30",
                    onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                    value: filterParams.status,
                    options: [{ label: "Todas", value: "" }, ...(statusOptions || [])],
                    placeholder: "Status",
                    dataTestid: 'establishment_status_filter'
                  },
                  {
                    className: "min-w-40",
                    onChange: (commercialKind) => setFilterParams((prev) => ({ ...prev, commercialKind })),
                    value: filterParams.commercialKind,
                    options: [{ label: "Todas", value: "" }, ...(commercialKindOptions || [])],
                    placeholder: "Tipo de estabelecimento",
                  },
                  {
                    className: "min-w-40",
                    onInputChange: setSearchProductCategory,
                    onChange: (productCategory) => setFilterParams((prev) => ({ ...prev, productCategory })),
                    value: filterParams.productCategory,
                    options: [{ label: "Todos", value: "" }, ...(productCategoryOptions || [])],
                    placeholder: "Serviços",
                  },
                  {
                    className: 'min-w-40',
                    placeholder: 'Estado',
                    options: [{ label: 'Todas', value: '' }, ...stateOptions],
                    value: filterParams.state || '',
                    onChange: (e: any) => setFilterParams((prev) => ({ ...prev, state: e?.value ? e : null, city: null })),
                    onInputChange: setSearchState,
                    dataTestid: 'state_search'
                  },
                  {
                    className: 'min-w-40',
                    placeholder: 'Cidade',
                    options: [{ label: 'Todas', value: '' }, ...cityOptions],
                    value: filterParams.city || '',
                    onChange: (e: any) => setFilterParams((prev) => ({ ...prev, city: e?.value ? e : null })),
                    onInputChange: setSearchCity,
                    dataTestid: 'city_search'
                  },
                ]}
              />
            </div>
          </div>

          <Table
            columns={defaultColumns}
            isLoadingData={isLoadingEstablishmentCompanies}
            page={filterParams.page || 1}
            rows={establishmentCompanies}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default List;
